import { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNews } from '../../context/NewsContext';
import { useLang } from '../../context/LanguageContext';
import { timeAgo } from '../../utils/formatDate';
import { addSubscriber } from '../../services/subscriptionService';
import { getCategoryFallbackImage } from '../../utils/categoryImages';
import { isBrandedImage } from '../../utils/isBrandedImage';
import AdBanner from './AdBanner';

/* ── Latest News Widget ─────────────────────────────── */
const LatestWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { lang, t } = useLang();

  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" />
        {t('latestNews')}
      </div>
      <div className="sidebar-widget__body">
        {articles.slice(0, 6).map((article) => {
          const img = (article.image && !isBrandedImage(article.image))
            ? article.image
            : getCategoryFallbackImage(article.category, article.id, article.title, article.summary);
          const handleClick = () => navigate(`/article/${article.id}`);
          return (
            <div key={article.id} className="sidebar-news-item" onClick={handleClick}>
              <div className="sidebar-news-item__img">
                <img
                  src={img}
                  alt={article.title}
                  loading="lazy"
                  onError={(e) => { e.target.onerror = null; e.target.src = getCategoryFallbackImage(article.category, article.id, article.title, article.summary); }}
                />
              </div>
              <div className="sidebar-news-item__text">
                <p className="sidebar-news-item__headline">{article.title}</p>
                <span className="sidebar-news-item__time">{timeAgo(article.pubDate, lang.toLowerCase())}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Subscribe Widget ───────────────────────────────── */
const SubscribeWidget = memo(() => {
  const [email, setEmail] = useState('');
  const { t } = useLang();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const result = addSubscriber(email, '');
    if (result.success) {
      toast.success(t('subscribeSuccess'));
      setEmail('');
    } else {
      toast.info(t('subscribeDuplicate'));
    }
  };

  return (
    <div className="sidebar-widget" style={{
      background: 'linear-gradient(135deg, #1a0000 0%, #330000 100%)',
      border: '1px solid #CC000044',
    }}>
      <div className="sidebar-widget__title" style={{ color: '#FFD700' }}>
        <span className="sidebar-widget__bar" style={{ background: '#FFD700' }} />
        {t('newsletter')}
      </div>
      <div className="sidebar-widget__body">
        <p style={{ color: '#aaa', fontSize: '12px', marginBottom: '12px', lineHeight: 1.5 }}>
          {t('newsletterWidgetDesc')}
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              background: '#0a0a0a',
              border: '1px solid #333',
              color: '#fff',
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px',
              marginBottom: '8px',
            }}
          />
          <button
            type="submit"
            style={{
              width: '100%',
              background: '#CC0000',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#990000'}
            onMouseLeave={e => e.currentTarget.style.background = '#CC0000'}
          >
            {t('subscribeBtn')} →
          </button>
        </form>
      </div>
    </div>
  );
});

/* ── Recommended / सुझाई गई खबरें Widget ───────────── */
const RecommendedWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { lang, t } = useLang();

  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" style={{ background: '#9C27B0' }} />
        {t('recommended')}
      </div>
      <div className="sidebar-widget__body">
        {articles.slice(6, 10).map((article) => {
          const img = (article.image && !isBrandedImage(article.image))
            ? article.image
            : getCategoryFallbackImage(article.category, article.id, article.title, article.summary);
          const handleClick = () => navigate(`/article/${article.id}`);
          return (
            <div key={article.id} className="sidebar-news-item" onClick={handleClick}>
              <div className="sidebar-news-item__img">
                <img
                  src={img}
                  alt={article.title}
                  loading="lazy"
                  onError={(e) => { e.target.onerror = null; e.target.src = getCategoryFallbackImage(article.category, article.id, article.title, article.summary); }}
                />
              </div>
              <div className="sidebar-news-item__text">
                <p className="sidebar-news-item__headline">{article.title}</p>
                <span className="sidebar-news-item__time">{timeAgo(article.pubDate, lang.toLowerCase())}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Viral News / वायरल खबरें Widget ───────────────── */
const ViralWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const viral = articles.filter(a => a.image).slice(10, 15);

  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" style={{ background: '#FF6B35' }} />
        {t('viralNews')}
      </div>
      <div className="sidebar-widget__body">
        {viral.map((article, i) => (
          <div
            key={article.id}
            onClick={() => navigate(`/article/${article.id}`)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '8px 0',
              borderBottom: i < viral.length - 1 ? '1px solid var(--card-border)' : 'none',
              cursor: 'pointer',
            }}
          >
            <span style={{
              fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif",
              fontSize: '1.4rem',
              fontWeight: 700,
              color: i === 0 ? '#FF6B35' : 'var(--text-muted)',
              lineHeight: 1,
              flexShrink: 0,
              width: '22px',
              textAlign: 'center',
            }}>
              {i + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif",
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: 0,
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                {article.title}
              </p>
              <span style={{ fontSize: '10px', color: '#FF6B35', marginTop: '2px', display: 'block' }}>
                🔥 Viral
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

/* ── Article Right Sidebar ───────────────────────────── */
const ArticleRightSidebar = () => {
  const { allArticles } = useNews();

  return (
    <aside>
      {/* Latest news — first for immediate value */}
      <LatestWidget articles={allArticles} />

      {/* Newsletter — above ads */}
      <SubscribeWidget />

      {/* Ads (index 2 moved to LeftSidebar) */}
      <AdBanner size="300x250" index={0} />
      <AdBanner size="300x250" index={1} />

      {/* Recommended articles */}
      <RecommendedWidget articles={allArticles} />

      {/* Viral news */}
      <ViralWidget articles={allArticles} />
    </aside>
  );
};

export default ArticleRightSidebar;
